import { supabaseAnonKey, supabaseUrl } from './supabaseConfig';

type AuthProxyResponse = {
  success?: boolean;
  error?: string;
  token?: string;
};

const invokeAuthProxy = async (body: Record<string, string>): Promise<AuthProxyResponse> => {
  const response = await fetch(`${supabaseUrl}/functions/v1/auth-proxy`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({})) as AuthProxyResponse;
  if (!response.ok) {
    throw new Error(data.error || `Authentication request failed (${response.status})`);
  }
  return data;
};

/**
 * Normalizes phone number to a 10-digit format
 */
export const normalizePhoneForAuth = (phone: string): string => {
  let sanitized = phone.trim().replace(/[^\d]/g, '');
  if (sanitized.startsWith('91') && sanitized.length > 10) {
    sanitized = sanitized.substring(2);
  }
  return sanitized;
};

/**
 * Sends OTP to the given phone number via The Future University API
 */
export const sendOtp = async (phone: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const normalizedPhone = normalizePhoneForAuth(phone);
    if (normalizedPhone.length !== 10) {
      return { success: false, error: 'Please enter a valid 10-digit mobile number' };
    }

    const data = await invokeAuthProxy({ action: 'send', phone: normalizedPhone });

    if (data?.success) {
      return { success: true };
    }

    return { success: false, error: data?.error || 'Failed to send OTP' };
  } catch (error: unknown) {
    console.error('Send OTP Error:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unable to connect. Please try again.';
    return { success: false, error: errorMessage };
  }
};


/**
 * Verifies OTP for the given phone number via The Future University API
 */
export const verifyOtp = async (phone: string, otp: string): Promise<{ success: boolean; error?: string; token?: string }> => {
  try {
    const normalizedPhone = normalizePhoneForAuth(phone);
    
    if (!otp || otp.length < 4) {
      return { success: false, error: 'Please enter a valid OTP' };
    }

    const data = await invokeAuthProxy({ phone: normalizedPhone, otp });

    if (data?.success && data?.token) {
      return { success: true, token: data.token };
    }

    const errorMessage = data?.error || 'Invalid OTP';
    return { success: false, error: errorMessage };
  } catch (error: unknown) {
    console.error('Verify OTP Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Verification failed. Please try again.',
    };
  }
};
